import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "@/src/lib/hooks/api/use-api";
import Icon from "../custom/Icon";
import { Image } from "../custom/Image";
import { Button } from "../ui/button";
import DesktopNav from "./DesktopNav";
import MobileMenu from "./MobileMenu";

export default function Navbar() {
	const { t } = useTranslation();
	const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
	const { auth, cart, orders, admins } = useApi();

	const { data: session } = auth.useSession();
	const { data: cartData } = cart.getByUserId(session?.user?.id || "");
	const { data: ordersData } = orders.getByUserId({
		userId: session?.user?.id || "",
	});
	const { data: adminCheck } = admins.checkCurrentUserIsAdmin();

	const cartItemCount = cartData?.cartItems?.length || 0;

	// Filter active orders (received, preparing, out-for-delivery)
	const activeOrders =
		ordersData?.orders?.filter((order) =>
			["received", "preparing", "out-for-delivery"].includes(order.status),
		) || [];

	const hasActiveOrders = activeOrders.length > 0;

	return (
		<>
			<nav className="fixed top-0 gap-4 h-(--navbar-height) w-full z-50 bg-background flex items-center justify-between px-6">
				{/* Left side - Logo */}
				<Link to="/" className="flex items-center gap-2 min-w-0">
					<Image
						src="/static/logo.webp"
						alt={t("admin.navbar.logo_alt")}
						className="w-16 shrink-0"
					/>
					<div className="min-w-0">
						<h3 className="text-lg font-semibold truncate">
							{t("common.brand_name")}
						</h3>
						<p className="text-sm text-primary font-medium hidden sm:block">
							{t("common.brand_tagline")}
						</p>
					</div>
				</Link>

				{/* Right side - Desktop Navigation + Mobile Icons + Mobile Menu Trigger */}
				<div className="flex items-center gap-4">
					{/* Desktop Navigation */}
					<div className="hidden md:block">
						<DesktopNav />
					</div>

					{/* Mobile Cart and Orders Icons */}
					<div className="flex items-center gap-4 md:hidden">
						{hasActiveOrders && (
							<Link to="/orders" className="relative">
								<Icon
									name="Package"
									className="size-5 text-chart-1 hover:text-chart-1/80 transition-colors"
								/>
								<span className="absolute -top-2 -right-2 bg-chart-1 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
									{activeOrders.length > 99 ? "99+" : activeOrders.length}
								</span>
							</Link>
						)}
						{!adminCheck?.isAdmin && (
							<Link to="/cart" className="relative">
								<Icon
									name="ShoppingCart"
									className="size-5 text-muted-foreground hover:text-primary transition-colors"
								/>
								{cartItemCount > 0 && (
									<span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
										{cartItemCount > 99 ? "99+" : cartItemCount}
									</span>
								)}
							</Link>
						)}
					</div>

					{/* Mobile Hamburger Menu */}
					<Button
						variant="ghost"
						size="icon"
						className="md:hidden"
						onClick={() => setMobileSidebarOpen(true)}
					>
						<Icon name="Menu" className="size-6" />
						<span className="sr-only">{t("common.sr_open_menu")}</span>
					</Button>
				</div>
			</nav>

			{/* Mobile Menu */}
			<MobileMenu
				open={mobileSidebarOpen}
				onOpenChange={setMobileSidebarOpen}
			/>
		</>
	);
}
